import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import { brandingSchema } from "../../api/schemas";
import type { Branding } from "../../api/schemas";
import { usePreferences } from "../preferences/Preferences";
import { isReadableTenantAccent } from "../theme/tenantAccent";

/** CSS custom property that carries the accent color of the employer. */
export const brandAccentProperty = "--brand-accent";

/**
 * Loads the branding of the employer (`GET /branding`, T-108).
 *
 * @returns The query.
 */
export function useBranding(): UseQueryResult<Branding> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.branding,
    queryFn: ({ signal }) => client.get("/api/v1/branding", brandingSchema, { signal }),
    staleTime: 10 * 60_000,
  });

  return query;
}

/**
 * Applies the branding of the employer: the accent color of the light theme and the default
 * language for people who did not choose one. The dark theme, the app bar and the navigation keep
 * their tested brand colors; an accent with too little contrast falls back to the brand accent
 * (T-159).
 *
 * @param branding - The branding, once loaded.
 * @param root - Element that receives the color (the document root).
 */
export function useApplyBranding(branding: Branding | undefined, root: HTMLElement): void {
  const { applyDefaultLocale } = usePreferences();
  const requested = branding?.accentColor ?? null;
  const color = requested !== null && isReadableTenantAccent(requested) ? requested : null;
  const language = branding?.defaultLanguage ?? null;

  useEffect(() => {
    if (color === null) {
      root.style.removeProperty(brandAccentProperty);
    } else {
      root.style.setProperty(brandAccentProperty, color);
    }

    return (): void => {
      root.style.removeProperty(brandAccentProperty);
    };
  }, [color, root]);

  useEffect(() => {
    if (language !== null) {
      applyDefaultLocale(language);
    }
  }, [language, applyDefaultLocale]);
}
